import { IconButton as DesignIconButton, Tooltip as DesignTooltip } from "@mui/material";
import { CloseOutlined as DesignClose, } from "@mui/icons-material";
import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

/**
 * Ch94 · DISMISS — a real dialog, replacing `window.prompt`.
 *
 * 🔴 WHY THE PROMPT HAD TO GO, beyond looking out of place:
 *  · it is unstyled OS chrome that cannot show the post being dismissed, so he
 *    is typing a reason for something he can no longer see;
 *  · it cannot be cancelled distinguishably — `window.prompt` returns null for
 *    Cancel and "" for an empty OK, and the old code treated both the same;
 *  · it blocks the whole page, and Safari can suppress it entirely after
 *    repeated use, which would silently make Dismiss stop working;
 *  · it offers no reasons, so every dismissal is free text nobody can count.
 *
 * The common reasons are offered as buttons because the point of recording a
 * reason is to LEARN from it later, and free text is not countable.
 */

/** The reasons worth counting. "Something else" keeps the free-text path. */
const COMMON_REASONS = [
  "Not actually looking to rent",
  "Outside Atlanta",
  "Already has an agent",
  "Too old to reply to now",
  "Not a real question",
];

interface Props {
  open: boolean;
  /** What is being dismissed, so he can see it while giving a reason. */
  postTitle: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

const DismissDialog: React.FC<Props> = ({ open, postTitle, onCancel, onConfirm }) => {
  const [reason, setReason] = React.useState("");

  React.useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const trimmed = reason.trim();

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 7, position: "relative" }}>Why are you dismissing this one?<span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={onCancel}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span></DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" data-testid="dismiss-subject">
            {postTitle}
          </Typography>

          <Stack direction="row" gap={0.75} flexWrap="wrap">
            {COMMON_REASONS.map((option) => (
              <Button
                key={option}
                size="small"
                variant={reason === option ? "contained" : "outlined"}
                onClick={() => setReason(option)}
                data-testid={`dismiss-reason-${option}`}
              >
                {option}
              </Button>
            ))}
          </Stack>

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Or say it your own way"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            data-testid="dismiss-reason-text"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} data-testid="dismiss-cancel">
          Keep it
        </Button>
        <Button
          color="error"
          variant="contained"
          // A reason is required, exactly as the old prompt required one — but
          // here the requirement is visible instead of a silent early return.
          disabled={!trimmed}
          onClick={() => onConfirm(trimmed)}
          data-testid="dismiss-confirm"
        >
          Dismiss it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DismissDialog;
